import {
    chakra,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    useMergeRefs,
    useMultiStyleConfig,
    SelectField,
} from "@chakra-ui/react";
import React, { useCallback, useState, forwardRef, useMemo } from "react";
import { useController } from "react-hook-form";

/**
 * Create a select field styled to Anvil specifications.
 *
 * The children of this elements should be the options to show in the dropdown.
 *
 * Valid props are:
 * - ref: Used to get access to the internal select field.
 * - name: The name of the select field.
 * - size: Used for theming, current implementation doesn't use this but might in the future
 * - variant: Used for theming, valid variants are currently: `floating-label`
 * - control: The control object provided by useForm, optional if <FormProvider> is used.
 *      See: https://react-hook-form.com/api/usecontroller
 * - fieldRules: The rules react-hook-form should add to the select. See: https://react-hook-form.com/api/usecontroller
 * - onChange: function to be called when the select field value is changed
 * - defaultValue: The default value for the select field.
 * - labelText: The text to use for the label of the field
 * - helpText: The help message to display under the field.
 * - errorText: Used to mark the field as having an error and the provided message will be displayed under the field.
 * - selectProps: Any additional props that should be added to the select field.
 * - isRequired: Whether or not the field is required.
 * - isDisabled: Whether or not the field is disabled.
 * - ...formControlProps: Any other properties passed will be used on the wrapper FormControl component.
 *
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export const AnvilSelect = forwardRef(function AnvilSelect(props, ref) {

    const {
        name,
        size,
        variant,
        control,
        fieldRules,
        onChange,
        defaultValue = '',
        labelText,
        helpText,
        errorText,
        selectProps,
        isRequired,
        isDisabled,
        children,
        ...formControlProps
    } = props;

    const {
        field: {
            ref: formHookRef,
            onChange: formHookOnChange,
            onBlur: formHookOnBlur,
            ...formHookSelectProps
        },
        fieldState: {error: fieldError},
    } = useController({
        name,
        rules: {
            required: isRequired,
            ...fieldRules
        },
        control,
        defaultValue,
    });
    // Combine the react-form-hook ref with the user provided one
    const refs = useMergeRefs(formHookRef, ref);
    // Combine input props from react-hook-form with the props passed by the user
    const allSelectProps = useMemo(
        () => ({
            ...formHookSelectProps,
            ...selectProps
        }),
        [formHookSelectProps, selectProps]
    );

    // The error message to show for the field.
    // If given will always use the error message from the `errorText` prop.
    // Else it will check to see if the field has an error with a message from `react-hook-form`.
    const errorMessage = useMemo(() => {
        if (errorText) {
            return errorText;
        }
        if (fieldError) {
            return fieldError.message;
        }
    }, [errorText, fieldError]);

    // Whether or not there is an error for the field.
    const hasError = useMemo(
        () => !!(errorText || fieldError),
        [errorText, fieldError]
    );

    const styles = useMultiStyleConfig("AnvilSelect", {
        size,
        variant
    });

    const [isFocused, setIsFocused] = useState(false);

    // Chakra has a bug where the FormControl group doesn't add the `data-` attributes which
    // are required to use the `_groupInvalid` and `_groupFocus` style selector we depend on.
    // https://github.com/chakra-ui/chakra-ui/issues/4271
    if (hasError) {
        formControlProps['data-invalid'] = true;
    }
    if (isDisabled) {
        formControlProps['data-disabled'] = true;
    }
    if (isFocused) {
        formControlProps['data-focus'] = true;
    }

    const setOnFocus = useCallback(() => {
        setIsFocused(true);
    }, []);

    const setOnBlur = useCallback(() => {
        formHookOnBlur();
        setIsFocused(false);
    }, [formHookOnBlur]);

    // Call the react-hook-form onChange callback and our custom one if provided
    const onChangeHandler = useCallback((e) => {
        formHookOnChange(e);
        if (onChange) {
            onChange(e);
        }
    }, [onChange, formHookOnChange]);

    return (
        <FormControl isInvalid={hasError} isRequired={isRequired} isDisabled={isDisabled} {...formControlProps}>
            <chakra.div __css={styles.wrapper}>
                <SelectField __css={styles.input}
                               onChange={onChangeHandler}
                               onFocus={setOnFocus}
                               onBlur={setOnBlur}
                               ref={refs}
                               disabled={isDisabled}
                               data-testid="anvil-select"
                               {...allSelectProps}
                >
                    {children}
                </SelectField>
                <FormLabel sx={styles.label}>
                    {labelText}
                </FormLabel>
            </chakra.div>
            {(!errorMessage && !!helpText) && <FormHelperText sx={styles.helpText}>{helpText}</FormHelperText>}
            <FormErrorMessage sx={styles.errorText}>{errorMessage}</FormErrorMessage>
        </FormControl>
    );
});
